import { Card, CardBody, Typography } from "@material-tailwind/react";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import { FC, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { getStats } from "redux/features/auth/authSlice";
import CommonLayout from "./DashboardMenu";
import {
  Users,
  UserCheck,
  Briefcase,
  PenSquare,
  Home,
  CalendarCheck,
  CalendarX,
  Euro,
  MessageSquare,
} from "lucide-react";
import React from "react";

// Animation variants for cards
const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
  hover: { scale: 1.04, transition: { duration: 0.3 }, boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)" },
};

// Animation variants for icons
const iconVariants = {
  hover: { scale: 1.2, transition: { duration: 0.3 } },
};

// Animation variants for the red dot
const redDotVariants = {
  initial: { scale: 1, opacity: 1 },
  animate: {
    scale: [1, 2, 1],
    opacity: [1, 0.7, 1],
    transition: {
      repeat: Infinity,
      duration: 1.5,
      ease: "easeInOut",
    },
  },
};

export interface AdminProfileProps {
  className?: string;
}

const AdminProfile: FC<AdminProfileProps> = React.memo(({ className = "" }) => {
  const dispatch = useAppDispatch();
  const stats = useAppSelector((state) => state?.auth?.stats);

  useEffect(() => {
    dispatch(getStats());
  }, [dispatch]);

  // Centralized card configuration
  const statisticCards = [
    {
      title: "Total clients",
      value: stats?.clientsCount ?? 0,
      icon: <Users className="w-6 h-6 text-white" />,
      href: "/admin/dashboard/users/clients",
    },
    {
      title: "Total propriétaires",
      value: stats?.ownersCount ?? 0,
      icon: <UserCheck className="w-6 h-6 text-white" />,
      href: "/admin/dashboard/users/owners",
    },
    {
      title: "Total prestataires",
      value: stats?.agentsCount ?? 0,
      icon: <Briefcase className="w-6 h-6 text-white" />,
      href: "/admin/dashboard/users/agents",
    },
    {
      title: "Total rédacteur",
      value: stats?.creatorsCount ?? 0,
      icon: <PenSquare className="w-6 h-6 text-white" />,
      href: "/admin/dashboard/users/creators",
    },
    {
      title: "Espaces en attente",
      value: stats?.inProgressLocations ?? 0,
      icon: <Home className="w-6 h-6 text-white" />,
      href: "/admin/dashboard/locations/unapproved",
    },
    {
      title: "Espaces non complétés",
      value: stats?.pendingLocations ?? 0,
      icon: <Home className="w-6 h-6 text-white" />,
      href: "/admin/dashboard/locations/incompleted",
    },
    {
      title: "Réservations en attente",
      value: stats?.pendingReservations ?? 0,
      icon: <CalendarCheck className="w-6 h-6 text-white" />,
      href: "/admin/dashboard/reservations/pending",
      showRedDot: stats?.isPending,
    },
    {
      title: "Réservations en cours",
      value: stats?.inProgressReservations ?? 0,
      icon: <CalendarCheck className="w-6 h-6 text-white" />,
      href: "/admin/dashboard/reservations/confirmed",
    },
    {
      title: "Réservations annulées",
      value: stats?.canceledReservationByClient ?? 0,
      icon: <CalendarX className="w-6 h-6 text-white" />,
      href: "/admin/dashboard/reservations/canceled/client",
    },
    {
      title: "Solde disponible",
      value: stats?.availableAmount ? `${stats.availableAmount} €` : "0 €",
      icon: <Euro className="w-6 h-6 text-white" />,
      href: "#",
    },
    {
      title: "Solde en attente",
      value: stats?.pengindAmount ? `${stats.pengindAmount} €` : "0 €",
      icon: <Euro className="w-6 h-6 text-white" />,
      href: "#",
    },
    {
      title: "Contacts",
      value: stats?.claimsCount ?? 0,
      icon: <MessageSquare className="w-6 h-6 text-white" />,
      href: "/admin/dashboard/reclamations",
    },
    {
      title: "Réclamations",
      value: stats?.reservationFeedbackCount ?? 0,
      icon: <MessageSquare className="w-6 h-6 text-white" />,
      href: "/admin/dashboard/reservations-reclamations",
    },
  ];

  return (
    <div className={`nc-AccountPage ${className}`} data-nc-id="AccountPage">
      <Helmet>
        <title>EventToBee | Location d'espaces : salles, lofts, jardins & boutiques – Paris & Île-de-France</title>
      </Helmet>

      <CommonLayout>
        <div className="py-20 space-y-12">
          <div className="container px-6 mx-auto max-w-7xl">
            <AnimatePresence>
              <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {statisticCards.map((card, index) => (
                  <motion.div
                    key={card.title}
                    variants={cardVariants}
                    initial="hidden"
                    animate="visible"
                    whileHover="hover"
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link to={card.href} aria-label={`Voir ${card.title}`}>
                      <Card className="relative w-full h-full transition-all duration-300 shadow-lg rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 dark:from-blue-600 dark:to-indigo-700 hover:from-blue-600 hover:to-indigo-700 dark:hover:from-blue-700 dark:hover:to-indigo-800 hover:shadow-xl">
                        <CardBody className="flex flex-col justify-between h-full p-6">
                          <div className="flex items-center justify-between">
                            <Typography className="text-base font-medium text-white">
                              {card.title}
                              {card.showRedDot && (
                                <motion.span
                                  className="absolute w-2 h-2 bg-red-500 rounded-full top-24 right-[15px]"
                                  variants={redDotVariants}
                                  initial="initial"
                                  animate="animate"
                                />
                              )}
                            </Typography>
                            <motion.div variants={iconVariants} whileHover="hover">
                              {card.icon}
                            </motion.div>
                          </div>
                          <Typography variant="h3" className="mt-4 font-bold text-white">
                            {card.value}
                          </Typography>
                        </CardBody>
                      </Card>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </AnimatePresence>
          </div>
        </div>
      </CommonLayout>
    </div>
  );
});

export default AdminProfile;
